import React from 'react';

import type { MediaContainer, MediaContainerDataItem } from '../../@types/item';
import VideoPlayer from '../video-player/video-player-selector';
import EmbedPreview from './embed-preview';
import ImageFullscreen from './image-fullscreen';
import ImagePreview from './image-preview';
import SphereFullscreen from './sphere-fullscreen';
import SpherePreview from './sphere-preview';
import ThreeSixtyFullscreen from './three-sixty-fullscreen';
import ThreeSixtyPreview from './three-sixty-preview';

const DEFAULT_FORMAT = '800x';
const FULLSCREEN_FORMAT = '1170x';
const SPHERE_LARGE_FORMAT = '1170x';
const SPHERE_PREVIEW_FORMAT = '800x800';

const sortOrder = (a: MediaContainerDataItem, b: MediaContainerDataItem) =>
  a.sortOrder - b.sortOrder;

const getFormatUrl = (file: MediaContainerDataItem, keyFormat: string) =>
  file.formats
    .filter((format) => format.format === keyFormat)
    .map((format) => format.url)
    .pop() || file.url;

const getFormatUrlWebp = (file: MediaContainerDataItem, keyFormat: string) =>
  file.formats
    .filter((format) => format.format === keyFormat)
    .map((format) => format.webp)
    .pop() || file.webp;

const getUrls = (files: MediaContainerDataItem[], keyFormat: string) =>
  (files || []).sort(sortOrder).map((file) => ({
    webp: getFormatUrlWebp(file, keyFormat),
    url: getFormatUrl(file, keyFormat),
  }));

const EmbedBuilder = (
  files: MediaContainerDataItem[],
  { proportions, imageAlt, onDisplayMediaInModal, index }: MediaAdditionals,
) => ({
  default: () => (
    <EmbedPreview
      key={index}
      url={files[0].url}
      proportions={proportions}
      imageAlt={imageAlt}
      onDisplayMediaInModal={onDisplayMediaInModal}
    />
  ),
  fullscreen: () => <VideoPlayer url={files[0].url} />,
});

const SphereBuilder = (
  files: MediaContainerDataItem[],
  { proportions, visible, onDisableNavigation, navigationDisabled, index }: MediaAdditionals,
) => ({
  default: () => (
    <SpherePreview
      key={index}
      url={getFormatUrl(files[0], SPHERE_LARGE_FORMAT)}
      preview={getFormatUrl(files[0], SPHERE_PREVIEW_FORMAT)}
      previewWebp={getFormatUrlWebp(files[0], SPHERE_PREVIEW_FORMAT)}
      proportions={proportions}
      visible={visible}
      onDisableNavigation={onDisableNavigation}
      navigationDisabled={navigationDisabled}
    />
  ),
  fullscreen: () => (
    <SphereFullscreen
      url={getFormatUrl(files[0], SPHERE_LARGE_FORMAT)}
      webp={getFormatUrlWebp(files[0], SPHERE_LARGE_FORMAT)}
    />
  ),
});

const ImageBuilder = (
  files: MediaContainerDataItem[],
  {
    proportions,
    visible,
    imageAlt,
    onDisableNavigation,
    navigationDisabled,
    onFullscreen,
    index,
  }: MediaAdditionals,
) => ({
  default: () =>
    files.length > 1 ? (
      <ThreeSixtyPreview
        key={index}
        urls={getUrls(files.sort(sortOrder), FULLSCREEN_FORMAT)}
        imageAlt={imageAlt}
        onDisableNavigation={onDisableNavigation}
        navigationDisabled={navigationDisabled}
        proportions={proportions}
        visible={visible}
      />
    ) : (
      <ImagePreview
        key={index}
        image={getFormatUrl(files[0], DEFAULT_FORMAT)}
        imageLarge={getFormatUrl(files[0], FULLSCREEN_FORMAT)}
        webp={getFormatUrlWebp(files[0], DEFAULT_FORMAT)}
        webpLarge={getFormatUrlWebp(files[0], FULLSCREEN_FORMAT)}
        imageAlt={imageAlt}
        proportions={proportions}
        onFullscreen={onFullscreen}
        index={index}
      />
    ),
  fullscreen: () =>
    files.length > 1 ? (
      <ThreeSixtyFullscreen
        urls={getUrls(files.sort(sortOrder), FULLSCREEN_FORMAT)}
        imageAlt={imageAlt}
      />
    ) : (
      <ImageFullscreen
        url={getFormatUrl(files[0], FULLSCREEN_FORMAT)}
        webp={getFormatUrlWebp(files[0], FULLSCREEN_FORMAT)}
        imageAlt={imageAlt}
      />
    ),
});

const DefaultImageBuilder = () => ({
  default: () => null,
  fullscreen: () => null,
});

const ParseMedia = (
  files: MediaContainerDataItem[],
  type: string,
  additional: MediaAdditionals,
) => {
  switch (type) {
    case 'Embedded':
      return EmbedBuilder(files, additional);
    case 'Sphere':
      return SphereBuilder(files, additional);
    case 'Image':
      return ImageBuilder(files, additional);
    default:
      return DefaultImageBuilder();
  }
};

interface MediaAdditionals {
  proportions: number;
  navigationDisabled: boolean;
  imageAlt: string;
  index: number;
  visible?: boolean;
  onDisableNavigation: () => void;
  onFullscreen: (index: number) => void;
  onDisplayMediaInModal: (displayMediaInModal: string) => void;
}

export class DefaultGalleryFactory {
  static parse = ({ files, type }: MediaContainer, additional: MediaAdditionals) =>
    ParseMedia(files, type, additional);
}

export default DefaultGalleryFactory;
